import React from 'react';
import { useTranslation } from 'react-i18next';
import Table from '@lib/components/Table/Table';
import { UseActionsResult } from 'routes/Sectors/Sector/utils/useActions';
import EmptyState from '@lib/components/EmptyState/EmptyState';
import { IconName } from '@lib/components/Modal/enums';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import UserModalForm from 'components/UserModalForm/UserModalForm';
import { Modals } from '@lib/enums/modals';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import { UseModalManagementResult } from '@lib/hooks/useModalManagement';
import useUsersActions from 'routes/Users/utils/useUsersActions';
import getUsersTableColumns from 'routes/Users/utils/getUsersTableColumns';
import getUsersTableActionsMenu from 'routes/Users/utils/getUsersTableActionsMenu';
import getUsersTableActionsForSelected from 'routes/Users/utils/getUsersTableActionsForSelected';

type Props = {
  isViewOnly: boolean;
  useActionsResult: UseActionsResult;
  useModalManagementResult: UseModalManagementResult;
};

export default function SectorUsers({
  isViewOnly,
  useActionsResult,
  useModalManagementResult,
}: Props) {
  const { t } = useTranslation();
  const { closeModal, isModalOpened } = useModalManagementResult;
  const {
    onEditHandler,
    onViewSectorHandler,
    onViewSiteHandler,
    onViewTeamHandler,
    useUsersBulkSelectResult,
    usersQueryHookResult,
  } = useActionsResult;

  const {
    currentUserId,
    onActivate,
    onBulkActivate,
    onBulkDeactivate,
    onConfirmedDelete,
    onConfirmedTeamMembersUpdate,
    onDeactivate,
    onDelete,
    onEdit,
    onResendEmailConfirmationHandler,
    onResendRegistrationConfirmationHandler,
    onSubmitUserFormCallback,
    onTeamMembersUpdate,
    openUserEditAccessesPage,
    openUserViewPage,
    selectedUserId,
    teamsLoadOptions,
  } = useUsersActions({
    useUsersQueryHookResult: usersQueryHookResult,
    useBulkSelectResult: useUsersBulkSelectResult,
    useModalManagementResult,
  });

  const columns = getUsersTableColumns({
    isViewOnly,
    currentUserId,
    onResendEmailConfirmationHandler,
    onResendRegistrationConfirmationHandler,
    onViewTeamHandler,
    onViewSiteHandler,
    onViewSectorHandler,
    t,
    showSiteSectorColumn: false,
  });
  const tableActionsMenu = getUsersTableActionsMenu({
    currentUserId,
    onActivate,
    onDeactivate,
    onDelete,
    onEdit,
    onView: openUserViewPage,
    onEditAccesses: openUserEditAccessesPage,
    t,
    isViewOnly,
  });
  const tableActionsForSelected = getUsersTableActionsForSelected({
    teamsLoadOptions,
    onBulkActivate,
    onBulkDeactivate,
    onTeamMembersUpdate,
    t,
    selectedItemsCount: useUsersBulkSelectResult.getSelectedItemsCount(
      usersQueryHookResult.totalCount,
    ),
    isViewOnly,
  });

  const { loading, collection, totalCount, firstLoading } =
    usersQueryHookResult;

  const isContentVisible = !!totalCount && !!collection.length;
  const isEmptyStateVisible = !isContentVisible && !firstLoading;

  if (firstLoading) {
    return <CircleLoader />;
  }

  if (isEmptyStateVisible) {
    return (
      <EmptyState
        title={t('no-linked-sector-users-yet')}
        text={t('no-linked-sector-users-text')}
        icon={IconName.Cancel}
        actions={[
          <Button
            key="edit"
            buttonText={t('edit-sector')}
            buttonType={ButtonTypes.primaryFilled}
            onClick={onEditHandler}
          />,
        ]}
      />
    );
  }

  return (
    <>
      <Table
        actionsForSelected={tableActionsForSelected}
        actionsMenu={tableActionsMenu}
        columns={columns}
        counterText={t('team-member', { count: totalCount })}
        data={collection}
        // fullHeight
        id="users"
        loading={loading}
        totalEntries={totalCount}
        getIsRowDisabled={({
          passwordSetupRequired,
          verified,
          deactivatedAt,
        }) => passwordSetupRequired || !verified || !!deactivatedAt}
        useBulkSelectResult={useUsersBulkSelectResult}
      />
      <UserModalForm
        isOpen={isModalOpened(Modals.UserFormModal)}
        onClose={() => {
          closeModal(Modals.UserFormModal);
          useUsersBulkSelectResult.handleSelectSingleTableItem(null);
        }}
        onSubmitUserFormCallback={onSubmitUserFormCallback}
        selectedUserId={selectedUserId}
        title={selectedUserId ? t('edit-team-member') : t('add-team-member')}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={t('delete-user-text')}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.UserDeleteModal)}
        onClose={() => closeModal(Modals.UserDeleteModal)}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
      />
      <ConfirmationModal
        confirmButtonLabel={t('yes-move-users')}
        description={t('move-users--to-team-text')}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.MoveMembersToTeamModal)}
        onClose={() => closeModal(Modals.MoveMembersToTeamModal)}
        onConfirm={onConfirmedTeamMembersUpdate}
        title={t('are-you-sure')}
      />
    </>
  );
}
